import * as React from 'react';
import { Navbar, FormGroup } from 'react-bootstrap';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import '../../../common/util/array.prototype';
import { BackgroundPref, NavItem, PrmPermissions, PrmService, User } from '../../../common';
import { CurrentUserService, JwtStorage } from '../../auth';
import { BackgroundPainter } from './BackgroundPainter';

import { HeaderNavBar } from './HeaderNavBar';
import { Logo } from './Logo';
import { UserProfile } from './UserProfile';
import { ErrorLogger } from '../error';
import { NavManager } from './NavManager';

interface HeaderProps extends RouteComponentProps<any> {
  appName: string;
  appSubtitle?: string;
  env: string;
  helpLink?: string;
  userLinks?: NavItem[];
  appSubNav?: NavItem[];
  contextUser?: User | User[];
  currentUserService: CurrentUserService;
  prmService: PrmService;
  jwtStorage: JwtStorage;
  errorLogger: ErrorLogger;
  navManager: NavManager;
  searchComponent?: JSX.Element;
  onLoading(loading: boolean): void;
}
interface HeaderState {
  bgClass: string;
  bgPref?: BackgroundPref;
  currentUser?: User;
  canImpersonate: boolean;
  navLinks: NavItem[];
}

const backgroundService = new BackgroundPainter();

class PageHeader extends React.Component<HeaderProps, HeaderState> {
  login = () => this.props.jwtStorage.login();

  changeBgPref = (bgPref?: BackgroundPref) => {
    if (bgPref) {
      const rotation = bgPref.backgroundRotation;
      const imageName = (rotation === 'NONE' && bgPref.backgroundPhoto) ?
        bgPref.backgroundPhoto :
        backgroundService.nextPhoto(rotation).name;
      const bgClass = 'bg-' + imageName.split('.')[0];

      this.setState({ bgClass });
    }

    this.setState({ bgPref });
  };

  constructor(props: HeaderProps) {
    super(props);

    this.state = {
      bgClass: 'bg-fall-leaves',
      canImpersonate: !this.props.env.startsWith('prod'),
      navLinks: []
    };
  }

  async findCurrentUser(): Promise<void> {
    const currentUser = await this.props.currentUserService.find();
    this.setState({ currentUser });
  }

  async findPref(): Promise<void> {
    const bgPref = await this.props.currentUserService.findPref();
    this.changeBgPref(bgPref);
  }

  async findPrmPermissions(): Promise<void> {
    if (this.props.env.startsWith('prod')) {
      return;
    }

    const prmPermissions = await this.props.currentUserService.findPermissions('PRM');
    const canImpersonate = prmPermissions.some(permission => permission.code === PrmPermissions.IMPERSONATE);
    this.setState({ canImpersonate });
  }

  async componentDidUpdate(prevProps: HeaderProps): Promise<void> {
    try {
      const contextUsers = Array.isArray(this.props.contextUser) ? this.props.contextUser : [this.props.contextUser];
      const prevContextUsers = Array.isArray(prevProps.contextUser) ? prevProps.contextUser : [prevProps.contextUser];

      if (
        this.state.navLinks.length === 0 ||
        this.props.location !== prevProps.location ||
        !contextUsers.same(prevContextUsers)
      ) {
        const tabs = await this.props.navManager.deriveTabs(this.props.contextUser);
        const navLinks = tabs.map(tab => {
          if (tab.text === this.props.appName) {
            return {
              ...tab,
              route: '/',
              children: this.props.appSubNav
            };
          }

          return tab;
        });

        this.setState({ navLinks });
      }
    } catch (err) {
      return this.props.errorLogger.log(err);
    }
  }

  async componentDidMount(): Promise<void> {
    try {
      await Promise.all([this.findCurrentUser(), this.findPref(), this.findPrmPermissions()]);
    } catch (err) {
      return this.props.errorLogger.log(err);
    }
  }

  render() {
    const helpLink = this.props.helpLink || 'mailto:gsit@ucdavis.edu';

    return (
      <div className={'layout-header-wrapper ' + this.state.bgClass}>
        {!this.props.env.startsWith('prod') ? ( <div className="development"/> ) : ''}

        <header className="layout-header">
          <div className="layout-header-top">
            <Logo appName={this.props.appName} appSubtitle={this.props.appSubtitle}/>

            <ul className="nav quick-links">
              <li><a href={helpLink} title="Help" target="_blank" rel="noopener">
                <i className="fa fa-question-circle"/></a></li>
            </ul>

            <UserProfile
              backgroundService={backgroundService}
              bgPref={this.state.bgPref}
              onChangeBg={this.changeBgPref}
              canImpersonate={this.state.canImpersonate}
              currentUser={this.state.currentUser}
              currentUserService={this.props.currentUserService}
              prmService={this.props.prmService}
              errorLogger={this.props.errorLogger}
              userLinks={this.props.userLinks}
              login={this.login}
            />
          </div>

          <Navbar fluid={true}>
            <Navbar.Header>
              <Navbar.Toggle>
                <span className="sr-only">Toggle navigation</span>
              </Navbar.Toggle>
            </Navbar.Header>

            <Navbar.Collapse>

              {this.state.navLinks && this.state.navLinks.length > 0 ?
                ( <HeaderNavBar links={this.state.navLinks}/> ) :
                ''
              }

              {
                this.props.searchComponent ? (
                  <Navbar.Form pullRight={true} style={{ padding: 0, border: 0 }}>
                    <FormGroup>
                      {this.props.searchComponent}
                    </FormGroup>
                  </Navbar.Form>
                ) : ''
              }

            </Navbar.Collapse>
          </Navbar>
        </header>
      </div>
    );
  }
}

export const Header = withRouter(PageHeader);



// WEBPACK FOOTER //
// ./src/gs-core/client/react/layout/Header.tsx